/* global flatpickr */

// Order cutoff hour (24h). Mirrors the theme setting `inventory_cutoff_hour`
// used on the product page (currently 12). Keep in sync if the merchant
// changes it in the theme editor.
const CUTOFF_HOUR = 12;

/**
 * True for Saturday (6) or Sunday (0).
 * @param {Date} date
 * @returns {boolean}
 */
function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/**
 * Earliest selectable delivery date derived from the current moment.
 * Pure: returns a new Date, never mutates the input.
 * @param {Date} now
 * @returns {Date}
 */
function computeMinDeliveryDate(now) {
  const day = now.getDay();
  const result = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Ordering on a weekend: earliest delivery is the upcoming Tuesday.
  if (day === 6) {
    result.setDate(result.getDate() + 3); // Sat -> Tue
    return result;
  }
  if (day === 0) {
    result.setDate(result.getDate() + 2); // Sun -> Tue
    return result;
  }

  // Weekday cut-off: before noon delivers tomorrow, from noon one extra day.
  const offset = now.getHours() < CUTOFF_HOUR ? 1 : 2;
  result.setDate(result.getDate() + offset);

  // A weekday minimum that lands on a weekend bumps to the following Monday.
  if (result.getDay() === 6) result.setDate(result.getDate() + 2);
  else if (result.getDay() === 0) result.setDate(result.getDate() + 1);

  return result;
}

if (!customElements.get('delivery-datepicker')) {
  class DeliveryDatepicker extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.js-delivery-input');
      this.hiddenInput = this.querySelector('.js-delivery-value');
      this.message = this.querySelector('.js-delivery-message');
      const formId = this.getAttribute('form');
      this.form = document.getElementById(formId);
      // The checkout button may live inside the form (cart page) or reference
      // it via a form attribute (cart drawer).
      this.checkoutBtn =
        document.querySelector(`[name="checkout"][form="${formId}"]`) ||
        (this.form && this.form.querySelector('[name="checkout"]'));
      this.submitHandler = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      if (this.form) this.form.addEventListener('submit', this.submitHandler);
      this.blockCheckout();
      this.initPicker();
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.submitHandler);
      if (this.picker) this.picker.destroy();
    }

    // Dims the checkout button without the native disabled attribute, so a
    // click still fires the submit guard and surfaces the validation message.
    blockCheckout() {
      if (!this.checkoutBtn) return;
      this.checkoutBtn.classList.add('is-delivery-blocked');
      this.checkoutBtn.setAttribute('aria-disabled', 'true');
    }

    unblockCheckout() {
      if (!this.checkoutBtn) return;
      this.checkoutBtn.classList.remove('is-delivery-blocked');
      this.checkoutBtn.removeAttribute('aria-disabled');
    }

    initPicker() {
      // Flatpickr is loaded as a static theme script before this file; guard in
      // case it is missing so the plain input still submits normally.
      if (typeof flatpickr === 'undefined') return;

      const minDate = computeMinDeliveryDate(new Date());

      this.picker = flatpickr(this.input, {
        minDate: minDate,
        dateFormat: 'd-m-Y',
        disableMobile: true, // enforce our rules instead of the native picker
        // Render the calendar in a wrapper next to the input instead of <body>.
        // The drawer is position:fixed with internal scroll and the theme
        // scroll-locks <body> (top offset) while it is open, which breaks
        // flatpickr's document-based positioning and threw the calendar
        // off-screen on scrolled pages.
        static: true,
        disable: [(date) => isWeekend(date)],
        // Prefer a persisted value; flatpickr ignores it if it's now stale.
        defaultDate: this.hiddenInput.value || minDate,
        onChange: (selectedDates, dateStr) => this.handleChange(dateStr),
      });

      // If no date was read from the value field (or the stored one was
      // rejected for being before minDate), fall back to the earliest valid
      // delivery date.
      if (!this.picker.selectedDates.length) {
        this.picker.setDate(minDate, false); // false = don't fire onChange
      }

      // Persist the resulting date and unblock checkout.
      this.hiddenInput.value = this.picker.formatDate(
        this.picker.selectedDates[0],
        'd-m-Y'
      );
      this.unblockCheckout();
    }

    handleChange(dateStr) {
      this.hiddenInput.value = dateStr;
      if (dateStr) {
        this.hideMessage();
        this.unblockCheckout();
      } else {
        this.blockCheckout();
      }
    }

    handleSubmit(evt) {
      if (!this.hiddenInput.value) {
        evt.preventDefault();
        this.showMessage();
        if (this.picker) this.picker.open();
      }
    }

    showMessage() {
      if (this.message) this.message.hidden = false;
    }

    hideMessage() {
      if (this.message) this.message.hidden = true;
    }
  }
  customElements.define('delivery-datepicker', DeliveryDatepicker);
}